import type { ChannelIssue } from '~publish/components/Composer/components/CollapsedChannelSection'
import { VALIDATION_CODE } from '~publish/legacy/validation/constants'

import type { ValidationFeedback } from '../stores/types'
import type { Notification } from '../values/Notification'

import { getSavingErrorMessage } from './getSavingErrorMessage'

// Instagram media issues that ExpandedComposer surfaces via
// BypassValidationWithReminderMessage regardless of dirty state (the user
// needs to know before ever touching the draft that the attached media
// itself won't post automatically).
const INSTAGRAM_DIRTY_EXEMPT_CODES: ReadonlySet<string> = new Set([
  VALIDATION_CODE.ASPECT_RATIO,
  VALIDATION_CODE.VIDEO_DURATION,
])

export const getCollapsedChannelIssues = ({
  draftId,
  serviceName,
  whatPreventsSaving,
  visibleNotifications,
  isDirty,
}: {
  draftId: string
  serviceName: string
  whatPreventsSaving: ValidationFeedback[]
  visibleNotifications: Notification[]
  isDirty: boolean
}): ChannelIssue[] => {
  // Client-side validation failures for this channel (live, pre-save) surface as
  // warnings. Entries with an empty composerId are global and intentionally excluded.
  // Suppressed until the draft is dirty, mirroring EditorNotices, except for the
  // Instagram media issues ExpandedComposer always shows via
  // BypassValidationWithReminderMessage.
  const validationIssues: ChannelIssue[] = whatPreventsSaving
    .filter((what) => what.composerId === draftId)
    .filter(
      (what) =>
        isDirty ||
        (serviceName === 'instagram' &&
          INSTAGRAM_DIRTY_EXEMPT_CODES.has(what.code)),
    )
    .map((what) => ({ message: what.message, severity: 'warning' }))

  // Server-side inline save failure for this channel (post-save-attempt) is
  // critical, normalized to the same copy the expanded composer shows via
  // useSavingError (empty -> fallback, raw duplicate-post text -> friendly copy,
  // queue limit). Disjoint in time from validation, so a plain concat needs no dedup.
  const serverErrorMessage = getSavingErrorMessage(
    visibleNotifications,
    serviceName,
  )

  return [
    ...validationIssues,
    ...(serverErrorMessage
      ? [{ message: serverErrorMessage, severity: 'critical' as const }]
      : []),
  ]
}
